import React, { useState } from 'react'

import { FieldIcon } from 'features/admin/fields/icons/FieldIcon'
import { getFieldIcon } from 'features/admin/fields/icons/utils'

import useDebounce from 'v2/ui/utils/useDebounce'

import { Box } from 'ui/components/Box'
import { Field } from 'ui/components/Field'
import { IconPickerDropdown } from 'ui/components/IconPicker'
import { IconValue } from 'ui/components/IconPicker/IconPickerDropdown'
import { Input } from 'ui/components/Input'
import { RadioCard, RadioCardGroup } from 'ui/components/Radio'
import { Textarea } from 'ui/components/Textarea'
import { Toggle } from 'ui/components/Toggle'
import { theme } from 'ui/styling/Theme.css'

import { FieldCollectionAttribute } from './types'
import { canFieldBeRenderedAsImage, canFieldBeRequired, canFieldBeTruncated } from './utils'

const DEBOUNCE_RATE = 300 // ms

type FieldCollectionDetailsControlProps = {
    field: FieldCollectionAttribute
    onChange: (patch: Partial<FieldCollectionAttribute>) => void
}

export const FieldCollectionDetailsControl: React.FC<FieldCollectionDetailsControlProps> = ({
    field: attribute,
    onChange,
}) => {
    const { field } = attribute

    const debouncedOnChange = useDebounce(onChange, DEBOUNCE_RATE) as typeof onChange

    const defaultTitle = field.label || ''
    const [localTitle, setLocalTitle] = useState(attribute.label || '')
    const onChangeLabel = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value || ''

        setLocalTitle(value)
        debouncedOnChange({ label: value })
    }

    const defaultIcon = getFieldIcon(field)
    const icon = attribute?.icon
    const onChangeIcon = (value?: IconValue) => {
        onChange({
            icon: value,
        })
    }

    const canBeRequired = field ? canFieldBeRequired(field) : false
    const isRequired = attribute.isRequired ?? false
    const onChangeIsRequired = (value: boolean) => {
        onChange({
            isRequired: value,
        })
    }

    const canBeTruncated = field ? canFieldBeTruncated(field) : false
    const disableTruncation = attribute.disableTruncation ?? false
    const onChangeDisableTruncation = (value: boolean) => {
        onChange({
            disableTruncation: value,
        })
    }

    const defaultDescription = attribute?.description || ''
    const [localDescription, setLocalDescription] = useState(defaultDescription)
    const onChangeDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value || ''
        setLocalDescription(value)
        debouncedOnChange({ description: value })
    }

    const descriptionPlacement = attribute?.descriptionPlacement || 'top'
    const onChangeDescriptionPlacement = (value: string) => {
        onChange({
            descriptionPlacement: value as FieldCollectionAttribute['descriptionPlacement'],
        })
    }

    const canBeRenderedAsImage = field ? canFieldBeRenderedAsImage(field) : false
    const renderAsImage = attribute.renderAsImage ?? false
    const onChangeRenderAsImage = (value: boolean) => {
        onChange({ renderAsImage: value })
    }

    return (
        <Box px="l" flex column gap="3xl" height="full" overflowY="auto">
            <Box flex alignItems="flex-start" gap="m">
                <Box noShrink>
                    <Field
                        label="Icon"
                        helperText={!!defaultIcon ? <FieldIcon value={defaultIcon} /> : undefined}
                    >
                        <IconPickerDropdown value={icon} onChange={onChangeIcon} />
                    </Field>
                </Box>
                <Box grow>
                    <Input
                        label="Label"
                        name="label"
                        size="m"
                        autoFocus
                        helperText={defaultTitle}
                        value={localTitle}
                        onChange={onChangeLabel}
                    />
                </Box>
            </Box>
            <Textarea
                size="m"
                label="Description"
                resizable
                value={localDescription}
                onChange={onChangeDescription}
            />
            {localDescription && (
                <Field label="Description placement">
                    <RadioCardGroup
                        value={descriptionPlacement}
                        onValueChange={onChangeDescriptionPlacement}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                            gap: theme.space.m,
                        }}
                    >
                        <RadioCard value="top" icon={{ name: 'ListStart' }}>
                            Next to label
                        </RadioCard>
                        <RadioCard value="bottom" icon={{ name: 'ArrowDownFromLine' }}>
                            Bottom
                        </RadioCard>
                    </RadioCardGroup>
                </Field>
            )}
            <Box flex column gap="l">
                {canBeRequired && (
                    <Field
                        htmlFor="isRequired"
                        label="Required"
                        rightSlotContent={
                            <Toggle
                                id="isRequired"
                                checked={isRequired}
                                onCheckedChange={onChangeIsRequired}
                            />
                        }
                    />
                )}
                {canBeTruncated && (
                    <Field
                        htmlFor="disableTruncation"
                        label="Disable truncation"
                        rightSlotContent={
                            <Toggle
                                id="disableTruncation"
                                checked={disableTruncation}
                                onCheckedChange={onChangeDisableTruncation}
                            />
                        }
                    />
                )}
                {canBeRenderedAsImage && (
                    <Field
                        htmlFor="renderAsImage"
                        label="Display as image"
                        rightSlotContent={
                            <Toggle
                                id="renderAsImage"
                                checked={renderAsImage}
                                onCheckedChange={onChangeRenderAsImage}
                            />
                        }
                    />
                )}
            </Box>
        </Box>
    )
}
